import React, { useState, useEffect } from 'react';
import designApi from '@services/designApi.jsx';
import ResourceUploadField from '@components/common/ResourceUploadField.jsx';

const AmountSetting = ({ themeId, themePageMaster }) => {
  // 수량 선택 페이지 상태
  const [amountPage, setAmountPage] = useState({
    themePageMaster: themePageMaster,
    languageCode: 'ko',
    messageText: '',
    messageColor: '#FFFFFF',
    quantityTextColor: '#FFFFFF',
    minPrintQuantity: 1,
    maxPrintQuantity: 10,
    printPrice1cut: 4000,
    printPrice2cut: 4000,
    printPrice3cut: 4000,
    printPrice4cut: 4000,
    normalVideoPrice: 5000,
    specialVideoPrice: 6000,
    priceTextColor: '#FFFFFF',
    timerSeconds: 10,
    timerTimeoutRedirectUrl: 'start'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // API 호출로 데이터 가져오기
  useEffect(() => {
    const fetchAmountPage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await designApi.getAmountPage(themeId);
        
        setAmountPage({
          ...amountPage,
          ...data
        });

        console.log("API 호출 성공:", data);
      } catch (err) {
        console.error("API 호출 실패:", err);
        setError(err);
        // 에러 발생 시 기본 데이터 유지
      } finally {
        setIsLoading(false);
      }
    };

    fetchAmountPage();
  }, [themeId]);

  // 입력값 변경 핸들러
  const handleInputChange = (field, value) => {
    setAmountPage(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 가격 변경 핸들러
  const handlePriceChange = (type, value) => {
    const numValue = isNaN(Number(value)) ? 0 : Number(value);

    switch (type) {
      case 'oneCut':
        setAmountPage(prev => ({ ...prev, printPrice1cut: numValue }));
        break;
      case 'twoCut':
        setAmountPage(prev => ({ ...prev, printPrice2cut: numValue }));
        break;
      case 'threeCut':
        setAmountPage(prev => ({ ...prev, printPrice3cut: numValue }));
        break;
      case 'fourCut':
        setAmountPage(prev => ({ ...prev, printPrice4cut: numValue }));
        break;
      default:
        break;
    }
  };

  // 동영상 가격 변경 핸들러
  const handleVideoPriceChange = (type, value) => {
    const numValue = isNaN(Number(value)) ? 0 : Number(value);

    switch (type) {
      case 'normal':
        setAmountPage(prev => ({ ...prev, normalVideoPrice: numValue }));
        break;
      case 'special':
        setAmountPage(prev => ({ ...prev, specialVideoPrice: numValue }));
        break;
      default:
        break;
    }
  };

  // 수량 변경 핸들러
  const handleAmountChange = (type, value) => {
    const numValue = isNaN(Number(value)) ? 0 : Number(value);

    switch (type) {
      case 'minPrintAmount':
        setAmountPage(prev => ({ ...prev, minPrintQuantity: numValue }));
        break;
      case 'maxPrintAmount':
        setAmountPage(prev => ({ ...prev, maxPrintQuantity: numValue }));
        break;
      case 'timer':
        setAmountPage(prev => ({ ...prev, timerSeconds: numValue }));
        break;
      default:
        break;
    }
  };

  // 리소스 업로드 처리 함수
  const handleResourceUploaded = (resourceType, resourceId) => {
    setAmountPage({
      ...amountPage,
      [resourceType]: resourceId
    });
  };

  // 타이머 조절 핸들러
  const handleTimerDecrease = () => {
    setAmountPage(prev => ({
      ...prev,
      timerSeconds: Math.max((prev.timerSeconds || 10) - 1, 1)
    }));
  };

  const handleTimerIncrease = () => {
    setAmountPage(prev => ({
      ...prev,
      timerSeconds: (prev.timerSeconds || 10) + 1
    }));
  };

  // 저장 이벤트 리스너 (페이지 상위 컴포넌트에서 저장 요청 시 실행)
  useEffect(() => {
    const eventName = `savePageDesign_amount`; // 각 컴포넌트마다 고유한 이벤트 이름

    const saveHandler = async (event) => {
      try {
        // 저장 중임을 표시
        setIsLoading(true);

        // 자체 로직을 사용하여 데이터 저장
        const isSucceed = await designApi.saveAmountPage(event.detail.themeId, amountPage);

        if(isSucceed) {
          // 성공 메시지
          alert("저장되었습니다.");
        } else {
          // 실패 메시지
          alert("저장에 실패했습니다.");
        }

      } catch (error) {
        // 오류 처리
        console.error("저장 실패:", error);
        alert("저장 중 오류가 발생했습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    // 이벤트 리스너 등록
    document.addEventListener(eventName, saveHandler);

    // 컴포넌트 언마운트 시 리스너 제거
    return () => {
      document.removeEventListener(eventName, saveHandler);
    };
  }, [amountPage, themeId]); // 의존성 배열에 필요한 상태 포함

  return (
    <div className="design-settings">
      {/* 언어 선택 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">언어 선택</h4>
        <div className="option-select-container">
          <select
            className="option-select"
            value={amountPage.languageCode || "ko"}
            onChange={(e) =>
              handleInputChange('languageCode', e.target.value)
            }
          >
            <option value="ko">한국어</option>
            <option value="en">영어</option>
            <option value="jp">일본어</option>
            <option value="cn">중국어</option>
          </select>
          <i className="ri-arrow-down-s-line select-arrow"></i>
        </div>
      </div>

      {/* 배경 이미지 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 이미지</h4>
        <ResourceUploadField
          label=""
          resourceId={amountPage.backgroundResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("backgroundResourceId", id)
          }
          acceptTypes="image/*"
        />
      </div>

      {/* 배경 음악 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">배경 음악</h4>
        <ResourceUploadField
          label=""
          resourceId={amountPage.backgroundMusicResourceId}
          onResourceUploaded={(id) =>
            handleResourceUploaded("backgroundMusicResourceId", id)
          }
          acceptTypes="audio/*"
        />
      </div>

      {/* 문구 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">문구</h4>
        <div className="settings-row">
          <div className="settings-input">
            <input 
              type="text" 
              placeholder="문구를 입력해주세요" 
              className="text-input"
              value={amountPage.messageText || ""}
              onChange={(e) => handleInputChange('messageText', e.target.value)}
            />
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={amountPage.messageColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={amountPage.messageColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('messageColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* 인쇄 수량 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">인쇄수량</h4>
        <div className="settings-row">
          <div className="settings-label">배경이미지</div>
          <ResourceUploadField
            label=""
            resourceId={amountPage.quantityBackgroundResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("quantityBackgroundResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
        
        <div className="settings-row">
          <div className="settings-label">수량 글자 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={amountPage.quantityTextColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={amountPage.quantityTextColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('quantityTextColor', e.target.value)}
            />
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-label">최소 인쇄 수량</div>
          <div className="amount-control-container">
            <button 
              className="amount-btn amount-btn-decrease"
              onClick={() => handleAmountChange('minPrintAmount', Math.max(1, (amountPage.minPrintQuantity || 1) - 1))}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="amount-input" 
              value={amountPage.minPrintQuantity || 1}
              onChange={(e) => handleAmountChange('minPrintAmount', parseInt(e.target.value) || 1)}
            />
            <button 
              className="amount-btn amount-btn-increase"
              onClick={() => handleAmountChange('minPrintAmount', (amountPage.minPrintQuantity || 1) + 1)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">매</span>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-label">최대 인쇄 수량</div>
          <div className="amount-control-container">
            <button 
              className="amount-btn amount-btn-decrease"
              onClick={() => handleAmountChange('maxPrintAmount', Math.max((amountPage.minPrintQuantity || 1), (amountPage.maxPrintQuantity || 10) - 1))}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="amount-input" 
              value={amountPage.maxPrintQuantity || 10}
              onChange={(e) => handleAmountChange('maxPrintAmount', parseInt(e.target.value) || (amountPage.minPrintQuantity || 1))}
            />
            <button 
              className="amount-btn amount-btn-increase"
              onClick={() => handleAmountChange('maxPrintAmount', (amountPage.maxPrintQuantity || 10) + 1)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">매</span>
          </div>
        </div>
      </div>

      {/* 인쇄 금액 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">인쇄금액</h4>
        <div className="settings-row">
          <div className="settings-label">1컷</div>
          <div className="price-control-container">
            <button 
              className="price-control-btn decrease-btn"
              onClick={() => handlePriceChange('oneCut', Math.max(0, (amountPage.printPrice1cut || 4000) - 1000))}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="price-input" 
              value={amountPage.printPrice1cut || 4000}
              onChange={(e) => handlePriceChange('oneCut', parseInt(e.target.value) || 0)}
            />
            <button 
              className="price-control-btn increase-btn"
              onClick={() => handlePriceChange('oneCut', (amountPage.printPrice1cut || 4000) + 1000)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">원</span>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-label">2컷</div>
          <div className="price-control-container">
            <button 
              className="price-control-btn decrease-btn"
              onClick={() => handlePriceChange('twoCut', Math.max(0, (amountPage.printPrice2cut || 4000) - 100))}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="price-input" 
              value={amountPage.printPrice2cut || 4000}
              onChange={(e) => handlePriceChange('twoCut', parseInt(e.target.value) || 0)}
            />
            <button 
              className="price-control-btn increase-btn"
              onClick={() => handlePriceChange('twoCut', (amountPage.printPrice2cut || 4000) + 100)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">원</span>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-label">3컷 (1+1)</div>
          <div className="price-control-container">
            <button 
              className="price-control-btn decrease-btn"
              onClick={() => handlePriceChange('threeCut', Math.max(0, (amountPage.printPrice3cut || 4000) - 100))}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="price-input" 
              value={amountPage.printPrice3cut || 4000}
              onChange={(e) => handlePriceChange('threeCut', parseInt(e.target.value) || 0)}
            />
            <button 
              className="price-control-btn increase-btn"
              onClick={() => handlePriceChange('threeCut', (amountPage.printPrice3cut || 4000) + 100)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">원</span>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-label">4컷 (1+1)</div>
          <div className="price-control-container">
            <button 
              className="price-control-btn decrease-btn"
              onClick={() => handlePriceChange('fourCut', Math.max(0, (amountPage.printPrice4cut || 4000) - 100))}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="price-input" 
              value={amountPage.printPrice4cut || 4000}
              onChange={(e) => handlePriceChange('fourCut', parseInt(e.target.value) || 0)}
            />
            <button 
              className="price-control-btn increase-btn"
              onClick={() => handlePriceChange('fourCut', (amountPage.printPrice4cut || 4000) + 100)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">원</span>
          </div>
        </div>
      </div>

      {/* 동영상 금액 설정 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">동영상 금액 설정</h4>
        <div className="settings-row">
          <div className="settings-label">일반 동영상</div>
          <div className="price-control-container">
            <button 
              className="price-control-btn decrease-btn"
              onClick={() => handleVideoPriceChange('normal', Math.max(0, (amountPage.normalVideoPrice || 5000) - 1000))}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="price-input" 
              value={amountPage.normalVideoPrice || 5000}
              onChange={(e) => handleVideoPriceChange('normal', parseInt(e.target.value) || 0)}
            />
            <button 
              className="price-control-btn increase-btn"
              onClick={() => handleVideoPriceChange('normal', (amountPage.normalVideoPrice || 5000) + 1000)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">원</span>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-label">스페셜 동영상</div>
          <div className="price-control-container">
            <button 
              className="price-control-btn decrease-btn"
              onClick={() => handleVideoPriceChange('special', Math.max(0, (amountPage.specialVideoPrice || 6000) - 1000))}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="price-input" 
              value={amountPage.specialVideoPrice || 6000}
              onChange={(e) => handleVideoPriceChange('special', parseInt(e.target.value) || 0)}
            />
            <button 
              className="price-control-btn increase-btn"
              onClick={() => handleVideoPriceChange('special', (amountPage.specialVideoPrice || 6000) + 1000)}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="unit-text">원</span>
          </div>
        </div>
      </div>

      {/* 금액 설정 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">금액</h4>
        <div className="settings-row">
          <div className="settings-label">배경이미지</div>
          <ResourceUploadField
            label=""
            resourceId={amountPage.priceBackgroundResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("priceBackgroundResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">글자 색상</div>
          <div className="color-picker-container">
            <input 
              type="text" 
              className="color-input" 
              value={amountPage.priceTextColor || "#FFFFFF"} 
              readOnly 
            />
            <input 
              type="color" 
              className="color-picker" 
              value={amountPage.priceTextColor || "#FFFFFF"}
              onChange={(e) => handleInputChange('priceTextColor', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* +- 버튼 설정 섹션 */}
      <div className="settings-box">
        <h4 className="settings-title">금액 증감 버튼</h4>
        <div className="settings-row">
          <div className="settings-label">더하기 버튼</div>
          <ResourceUploadField
            label=""
            resourceId={amountPage.pricePlusButtonResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("pricePlusButtonResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
        <div className="settings-row">
          <div className="settings-label">빼기 버튼</div>
          <ResourceUploadField
            label=""
            resourceId={amountPage.priceMinusButtonResourceId}
            onResourceUploaded={(id) =>
              handleResourceUploaded("priceMinusButtonResourceId", id)
            }
            acceptTypes="image/*"
          />
        </div>
      </div>

      {/* 타이머 설정 섹션 */}
      {/* <div className="settings-box">
        <h4 className="settings-title">타이머</h4>
        <div className="settings-row">
          <div className="settings-label">타이머 시간</div>
          <div className="timer-control-container">
            <button 
              className="timer-btn" 
              onClick={handleTimerDecrease}
            >
              <i className="ri-arrow-down-s-fill"></i>
            </button>
            <input 
              type="text" 
              className="timer-input" 
              value={amountPage.timerSeconds || 10}
              onChange={(e) => handleAmountChange('timer', parseInt(e.target.value) || 1)}
            />
            <button 
              className="timer-btn"
              onClick={handleTimerIncrease}
            >
              <i className="ri-arrow-up-s-fill"></i>
            </button>
            <span className="timer-unit">초</span>
          </div>
        </div>
        <div className="settings-row">
          <div className="settings-label">타임 오버 이동 페이지</div>
          <div className="page-select-container">
            <select 
              className="page-select"
              value={amountPage.timerTimeoutRedirectUrl || "start"}
              onChange={(e) => handleInputChange('timerTimeoutRedirectUrl', e.target.value)}
            >
              <option value="start">시작화면</option>
              <option value="angle">앵글선택</option>
              <option value="layout">레이아웃</option>
            </select>
            <i className="ri-arrow-down-s-line select-arrow"></i>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default AmountSetting;